import {
  ChatEventPreviewResult,
  ChatListPreviewActionCode,
  ChatListPreviewKind,
  ChatListPreviewModel,
} from '@chat/interfaces/chat-list-preview.interfaces';
import { CHAT_CALL_STATUS } from '@chat/interfaces/chat.interfaces';
import { isForwardedMessageData } from '@chat/utils/chat-forward.utils';
import { EventMessage } from '@services/web-socket.interfaces';

const CHAT_PREVIEW_FILES_AND_TEXT_PREFIX: string = 'lastMessageIsFilesWithText:';
const CHAT_PREVIEW_FORWARDED_PREFIX: string = 'forwardedPreview:';

export function buildChatEventPreview(data: EventMessage): ChatEventPreviewResult {
  const messageText = normalizeChatPreviewText(data?.message);
  const isCallServiceMessage = isCallMessageCode(messageText);
  const truncatedText = truncateChatPreviewText(messageText);
  const hasText = truncatedText.length > 0;
  const isVoiceMessage = data?.data?.voice === true;
  const hasFiles = hasFilesInEventMessage(data);
  const isForwardedMessage = isForwardedMessageData(data?.data);

  const preview: ChatListPreviewModel = hasText
    ? (
      hasFiles
        ? createChatPreviewModel(ChatListPreviewActionCode.LAST_MESSAGE_IS_FILES, {
          kind: ChatListPreviewKind.FILES_WITH_TEXT,
          isForwarded: isForwardedMessage,
          filesText: truncatedText,
        })
        : createChatPreviewModel(buildLastMessagePreview(truncatedText), {
          isForwarded: isForwardedMessage,
        })
    )
    : createChatPreviewModel(
      isVoiceMessage
        ? ChatListPreviewActionCode.VOICE_MESSAGE
        : (hasFiles ? ChatListPreviewActionCode.LAST_MESSAGE_IS_FILES : ChatListPreviewActionCode.NONE),
      {
        kind: hasFiles ? ChatListPreviewKind.FILES_ONLY : ChatListPreviewKind.DEFAULT,
        isForwarded: isForwardedMessage,
      }
    );

  return {
    preview,
    isCallServiceMessage,
    isForwardedMessage,
  };
}

export function hasFilesInEventMessage(data: EventMessage): boolean {
  if (typeof data?.filesLength === 'number' && data.filesLength > 0) {
    return true;
  }

  if (Array.isArray(data?.files) && data.files.length > 0) {
    return true;
  }

  return Array.isArray(data?.data?.files) && data.data.files.length > 0;
}

export function buildLastMessagePreview(messageText: string): string {
  if (messageText === CHAT_CALL_STATUS.INCOMING || messageText === CHAT_CALL_STATUS.OUTGOING) {
    return messageText;
  }

  if (messageText === CHAT_CALL_STATUS.FAIL) {
    return CHAT_CALL_STATUS.OUTGOING;
  }

  return messageText;
}

export function parseChatPreview(value: string | null | undefined): ChatListPreviewModel {
  const raw = normalizeChatPreviewText(value);
  if (!raw) {
    return createChatPreviewModel(ChatListPreviewActionCode.NONE);
  }

  let normalized = raw;
  let isForwarded = false;

  if (normalized.startsWith(CHAT_PREVIEW_FORWARDED_PREFIX)) {
    isForwarded = true;
    normalized = normalizeChatPreviewText(normalized.slice(CHAT_PREVIEW_FORWARDED_PREFIX.length));
  }

  if (!normalized) {
    return createChatPreviewModel(ChatListPreviewActionCode.NONE, { isForwarded });
  }

  if (normalized.startsWith(CHAT_PREVIEW_FILES_AND_TEXT_PREFIX)) {
    const filesText = normalizeChatPreviewText(normalized.slice(CHAT_PREVIEW_FILES_AND_TEXT_PREFIX.length));
    return createChatPreviewModel(ChatListPreviewActionCode.LAST_MESSAGE_IS_FILES, {
      kind: filesText ? ChatListPreviewKind.FILES_WITH_TEXT : ChatListPreviewKind.FILES_ONLY,
      isForwarded,
      filesText,
    });
  }

  return createChatPreviewModel(normalized, {
    kind: normalized === ChatListPreviewActionCode.LAST_MESSAGE_IS_FILES
      ? ChatListPreviewKind.FILES_ONLY
      : ChatListPreviewKind.DEFAULT,
    isForwarded,
  });
}

export function serializeChatPreview(preview: ChatListPreviewModel): string {
  const actionCode = preview.actionCode;
  const filesText = preview.filesText;

  let serialized = '';

  if (preview.kind === ChatListPreviewKind.FILES_WITH_TEXT) {
    serialized = filesText
      ? `${CHAT_PREVIEW_FILES_AND_TEXT_PREFIX}${filesText}`
      : ChatListPreviewActionCode.LAST_MESSAGE_IS_FILES;
  } else if (preview.kind === ChatListPreviewKind.FILES_ONLY && !actionCode) {
    serialized = ChatListPreviewActionCode.LAST_MESSAGE_IS_FILES;
  } else {
    serialized = actionCode;
  }

  if (!preview.isForwarded || !serialized) {
    return serialized;
  }

  return `${CHAT_PREVIEW_FORWARDED_PREFIX}${serialized}`;
}

export function truncateChatPreviewText(text: string, maxLength: number = 255): string {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + '...';
}

function isCallMessageCode(message: unknown): message is CHAT_CALL_STATUS {
  return message === CHAT_CALL_STATUS.INCOMING
    || message === CHAT_CALL_STATUS.OUTGOING
    || message === CHAT_CALL_STATUS.FAIL;
}

function createChatPreviewModel(
  actionCode: string,
  options?: {
    kind?: ChatListPreviewKind;
    isForwarded?: boolean;
    filesText?: string;
  },
): ChatListPreviewModel {
  const normalizedActionCode = actionCode.trim();
  const kind = options?.kind ?? ChatListPreviewKind.DEFAULT;
  const isForwarded = options?.isForwarded === true;
  const filesText = options?.filesText?.trim() ?? '';

  const preview: ChatListPreviewModel = {
    raw: '',
    actionCode: normalizedActionCode,
    kind,
    isForwarded,
    filesText,
  };

  preview.raw = serializeChatPreview(preview);
  return preview;
}

function normalizeChatPreviewText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}
